import type { CSSProperties } from 'react';
import React, { createContext } from 'react';
import { noop } from '../utility/generic';

export type TimelineMarkersProviderProps = {
  children: React.ReactNode;
};

export type TodayMarkerType = {
  id?: number;
  type: 'Today';
  renderer: React.FunctionComponent<{
    styles: CSSProperties;
    date: number;
  }>;
  interval: number;
};
export type CustomMarkerType = {
  id?: number;
  type: 'Custom';
  renderer: React.FunctionComponent<{
    styles: CSSProperties;
    date: number;
  }>;
  date: number;
};
export type CursorMarkerType = {
  id?: number;
  type: 'Cursor';
  renderer: React.FunctionComponent<{
    styles: CSSProperties;
    date: number;
  }>;
};
export type Marker = TodayMarkerType | CustomMarkerType | CursorMarkerType;

export type TimelineMarkersContext = {
  markers: Marker[];
  subscribeMarker: (newMarker: Marker) => {
    unsubscribe: () => void;
    getMarker: () => Marker;
  };
  updateMarker: (marker: Marker) => void;
};

const defaultContextState: TimelineMarkersContext = {
  markers: [],
  subscribeMarker: (marker) => {
    console.warn('default subscribeMarker used');
    return {
      unsubscribe: noop,
      getMarker: () => marker,
    };
  },
  updateMarker: () => {
    console.warn('default updateMarker used');
  },
};

const { Consumer, Provider } =
  createContext<TimelineMarkersContext>(defaultContextState);

// REVIEW: is this the best way to manage ids?
let _id = 0;
const createId = () => {
  _id += 1;
  return _id + 1;
};

export class TimelineMarkersProvider extends React.Component<
  TimelineMarkersProviderProps,
  TimelineMarkersContext
> {
  handleSubscribeToMarker = (newMarker: Marker) => {
    newMarker = {
      ...newMarker,
      // REVIEW: in the event that we accept id to be passed to the Marker components, this line would override those
      id: createId(),
    };

    this.setState((state) => {
      return {
        markers: [...state.markers, newMarker],
      };
    });
    return {
      unsubscribe: () => {
        this.setState((state) => {
          return {
            markers: state.markers.filter(
              (marker) => marker.id !== newMarker.id,
            ),
          };
        });
      },
      getMarker: () => {
        return newMarker;
      },
    };
  };

  handleUpdateMarker = (updateMarker: Marker) => {
    const markerIndex = this.state.markers.findIndex(
      (marker) => marker.id === updateMarker.id,
    );
    if (markerIndex < 0) return;
    this.setState((state) => {
      const markers = state.markers.slice();
      markers[markerIndex] = updateMarker;
      return {
        markers,
      };
    });
  };

  state: TimelineMarkersContext = {
    markers: [],
    subscribeMarker: this.handleSubscribeToMarker,
    updateMarker: this.handleUpdateMarker,
  };

  render() {
    return <Provider value={this.state}>{this.props.children}</Provider>;
  }
}

export const TimelineMarkersConsumer = Consumer;
